/**
 * deriveMediaQualityState – shared quality assessment for media uploaders.
 *
 * Evaluates the cover image quality and returns a summary state
 * with human-readable labels and explanatory details.
 *
 * Now context-aware via ImageIntent – different thresholds for hero vs product.
 *
 * Reusable across: place, area, producer, product, activity, and other uploaders.
 */

import type { MediaAsset } from '@/lib/media/mediaTypes';
import { type ImageIntent, getIntentThresholds } from '@/lib/media/imageIntent';

export type MediaQualityState = 'good' | 'improvable' | 'missing';

export interface MediaQualityResult {
  state: MediaQualityState;
  /** Short summary label for inline display */
  label: string;
  /** Whether the label should be clickable (has details to show) */
  hasDetails: boolean;
  /** Human-readable explanation lines shown on click */
  details: string[];
  /** Cover width in px, if available */
  coverWidth: number | null;
}

export function deriveMediaQualityState(
  coverAsset: MediaAsset | null,
  /** Image intent — required, no silent hero default. */
  intent: ImageIntent,
): MediaQualityResult {
  const thresholds = getIntentThresholds(intent);

  // No cover at all
  if (!coverAsset) {
    return {
      state: 'missing',
      label: 'Saknas',
      hasDetails: false,
      details: [],
      coverWidth: null,
    };
  }

  const w = coverAsset.width;

  // No width metadata
  if (w == null) {
    return {
      state: 'improvable',
      label: 'Kan förbättras',
      hasDetails: true,
      details: [
        'Det gick inte att läsa bildens storlek.',
        `Rekommenderat är minst ${thresholds.recommendedWidth}px bredd för ${thresholds.contextLabel}.`,
      ],
      coverWidth: null,
    };
  }

  if (w >= thresholds.goodWidth) {
    return {
      state: 'good',
      label: 'Bra',
      hasDetails: false,
      details: [],
      coverWidth: w,
    };
  }

  if (w >= thresholds.warnWidth) {
    return {
      state: 'improvable',
      label: 'Kan förbättras',
      hasDetails: true,
      details: [
        `Bilden är ${w}px bred – den fungerar bra som ${thresholds.contextLabel}.`,
        `För bästa resultat rekommenderas minst ${thresholds.recommendedWidth}px bredd.`,
      ],
      coverWidth: w,
    };
  }

  return {
    state: 'improvable',
    label: 'Kan förbättras',
    hasDetails: true,
    details: [
      `Bilden är ${w}px bred – den kan se något suddig som ${thresholds.contextLabel}.`,
      `Rekommenderat är minst ${thresholds.recommendedWidth}px bredd.`,
      'Prova att ladda upp en bild med högre upplösning.',
    ],
    coverWidth: w,
  };
}
